import { useNavigate } from 'react-router-dom';
import SeoHead from '../../components/SeoHead';

const sections = [
  {
    id: 1,
    icon: 'ri-translate-2',
    color: 'rose',
    title: 'Multilingüismo y diversidad lingüística',
    content: [
      {
        subtitle: 'Los cuatro idiomas oficiales',
        text: 'Suiza tiene cuatro idiomas oficiales: alemán (63%), francés (23%), italiano (8%) e italiano romanche (1%). La región donde trabajes determinará el idioma principal. Zúrich, Berna y Basilea son de habla alemana (Schweizerdeutsch); Ginebra y Lausana son francófonas; Lugano es italófona. Conocer el idioma de la región es una ventaja competitiva enorme.',
      },
      {
        subtitle: 'El inglés como lengua franca',
        text: 'En empresas multinacionales y en el sector financiero, tecnológico y farmacéutico, el inglés es el idioma de trabajo habitual. Sin embargo, en empresas locales o en la administración pública, el idioma regional es imprescindible. Investiga bien el entorno de la empresa antes de asumir que el inglés será suficiente.',
      },
      {
        subtitle: 'El Schweizerdeutsch',
        text: 'El alemán suizo (Schweizerdeutsch) es un dialecto oral muy diferente del alemán estándar. No te preocupes si no lo entiendes al principio: en entornos profesionales se usa el alemán estándar (Hochdeutsch) por escrito y en reuniones formales. Con el tiempo irás comprendiendo el dialecto, pero no se espera que lo hables.',
      },
    ],
  },
  {
    id: 2,
    icon: 'ri-shield-check-line',
    color: 'emerald',
    title: 'Precisión, calidad y neutralidad',
    content: [
      {
        subtitle: 'El estándar suizo de calidad',
        text: 'Suiza es sinónimo de precisión y excelencia. En el entorno laboral, esto se traduce en atención al detalle, cumplimiento riguroso de plazos y un estándar de calidad muy alto en todo lo que se produce. No presentes trabajo a medias; si necesitas más tiempo para hacerlo bien, comunícalo con antelación.',
      },
      {
        subtitle: 'Neutralidad y discreción',
        text: 'La neutralidad suiza no es solo política; también es cultural. En el trabajo, evita posicionamientos extremos, comentarios polémicos o debates sobre política y religión. La discreción es un valor muy apreciado, especialmente en el sector financiero y en empresas con clientes internacionales.',
      },
      {
        subtitle: 'Consenso y toma de decisiones',
        text: 'El sistema político suizo de democracia directa influye en la cultura empresarial: se busca el consenso antes de tomar decisiones importantes. Las reuniones pueden ser largas porque se escucha a todos los implicados. Una vez alcanzado el consenso, la implementación es rápida y comprometida.',
      },
    ],
  },
  {
    id: 3,
    icon: 'ri-building-2-line',
    color: 'sky',
    title: 'Sectores clave y mercado laboral',
    content: [
      {
        subtitle: 'Los sectores más demandantes de talento',
        text: 'Suiza destaca en: sector financiero y bancario (Zúrich, Ginebra), industria farmacéutica y biotecnología (Basilea, Zúrich), tecnología e ingeniería de precisión, relojería de lujo (Jura, Neuchâtel), organizaciones internacionales (Ginebra: ONU, OMS, Cruz Roja) y turismo de alta gama.',
      },
      {
        subtitle: 'Permiso de trabajo y residencia',
        text: 'Los ciudadanos de la UE/EEE pueden trabajar en Suiza con relativa facilidad gracias al Acuerdo de Libre Circulación. Necesitarás registrarte en la municipalidad (Gemeinde/commune) dentro de los 14 días de llegada. Para contratos de más de 3 meses, se requiere el permiso B (residencia temporal). Tu empleador suele gestionar gran parte del proceso.',
      },
      {
        subtitle: 'Salarios y coste de vida',
        text: 'Suiza tiene los salarios más altos de Europa, pero también uno de los costes de vida más elevados. El salario medio bruto es de unos 6.500-7.000 CHF/mes. Zúrich y Ginebra son especialmente caras. Negocia siempre en francos suizos (CHF) y ten en cuenta que el alquiler puede suponer el 30-40% del salario neto.',
      },
    ],
  },
  {
    id: 4,
    icon: 'ri-user-settings-line',
    color: 'amber',
    title: 'Cultura corporativa y relaciones profesionales',
    content: [
      {
        subtitle: 'Formalidad y respeto',
        text: 'El entorno laboral suizo es formal, especialmente en los primeros contactos. Usa el apellido y el tratamiento formal hasta que te inviten a tutear. Los títulos académicos se usan en contextos formales. La puntualidad es tan importante como en Alemania: llegar tarde es una falta de respeto.',
      },
      {
        subtitle: 'Trabajo en equipo y autonomía',
        text: 'Los suizos valoran tanto el trabajo en equipo como la autonomía individual. Se espera que seas capaz de gestionar tus tareas de forma independiente, pero también que colabores activamente cuando el proyecto lo requiere. La iniciativa propia es muy valorada, siempre que esté bien fundamentada.',
      },
      {
        subtitle: 'Networking y relaciones a largo plazo',
        text: 'En Suiza, las relaciones profesionales se construyen lentamente pero son muy duraderas. El networking es importante, especialmente en ciudades como Zúrich y Ginebra. Participa en eventos del sector, asociaciones profesionales y cámaras de comercio. Una recomendación personal vale más que cualquier candidatura espontánea.',
      },
    ],
  },
  {
    id: 5,
    icon: 'ri-heart-pulse-line',
    color: 'teal',
    title: 'Sistema social, bienestar y vida cotidiana',
    content: [
      {
        subtitle: 'Sistema de salud',
        text: 'En Suiza el seguro médico es obligatorio y privado. Debes contratar una póliza básica (Grundversicherung/assurance de base) dentro de los 3 meses de llegada. El coste mensual varía entre 300 y 500 CHF según el cantón y la franquicia elegida. Tu empleador no lo paga; es tu responsabilidad personal.',
      },
      {
        subtitle: 'Sistema de pensiones (3 pilares)',
        text: 'Suiza tiene un sistema de pensiones de 3 pilares: el AVS/AHV (obligatorio, cotización compartida empleado-empleador), el fondo de pensiones profesional (BVG/LPP, obligatorio para salarios superiores a 22.050 CHF/año) y el ahorro privado voluntario (3er pilar). Infórmate bien desde el inicio para optimizar tu jubilación.',
      },
      {
        subtitle: 'Calidad de vida',
        text: 'Suiza ofrece una calidad de vida excepcional: infraestructuras impecables, naturaleza espectacular, seguridad, sistema educativo de primer nivel y una oferta cultural muy rica. El transporte público es eficiente y puntual. El ritmo de vida es más tranquilo que en grandes capitales europeas, con un equilibrio real entre trabajo y vida personal.',
      },
    ],
  },
  {
    id: 6,
    icon: 'ri-map-pin-line',
    color: 'violet',
    title: 'Diferencias entre regiones suizas',
    content: [
      {
        subtitle: 'Suiza alemana (Deutschschweiz)',
        text: 'Zúrich, Berna, Basilea, Lucerna. Cultura más cercana a Alemania: directa, puntual, orientada al trabajo. El sector financiero, tecnológico y farmacéutico domina. Ambiente más reservado socialmente pero muy profesional. Ideal para perfiles de ingeniería, finanzas, tecnología y ciencias de la vida.',
      },
      {
        subtitle: 'Suiza francesa (Romandie)',
        text: 'Ginebra, Lausana, Neuchâtel. Cultura más mediterránea y abierta. Ginebra es el centro de las organizaciones internacionales (ONU, OMS, CICR) y del sector financiero y diplomático. Lausana alberga el Comité Olímpico Internacional. Ambiente más cosmopolita y diverso. El francés es imprescindible en muchos entornos.',
      },
      {
        subtitle: 'Suiza italiana (Ticino)',
        text: 'Lugano, Bellinzona, Locarno. La región más pequeña y con un carácter más mediterráneo. Lugano es un centro financiero importante. El italiano es el idioma de trabajo. El clima es más cálido y el ritmo de vida algo más relajado. Ideal para perfiles financieros y de gestión patrimonial.',
      },
    ],
  },
];

const tips = [
  { icon: 'ri-check-double-line', text: 'Contrata el seguro médico obligatorio en los primeros 3 meses' },
  { icon: 'ri-check-double-line', text: 'Regístrate en la municipalidad en los primeros 14 días' },
  { icon: 'ri-check-double-line', text: 'Aprende el idioma de la región donde vayas a trabajar' },
  { icon: 'ri-check-double-line', text: 'Investiga el coste de vida real antes de negociar el salario' },
  { icon: 'ri-check-double-line', text: 'Sé siempre puntual: la impuntualidad es muy mal vista' },
  { icon: 'ri-check-double-line', text: 'Infórmate sobre el sistema de 3 pilares de pensiones' },
];

const colorMap: Record<string, { bg: string; text: string; border: string; light: string }> = {
  rose: { bg: 'bg-rose-600', text: 'text-rose-600', border: 'border-rose-200', light: 'bg-rose-50' },
  emerald: { bg: 'bg-emerald-600', text: 'text-emerald-600', border: 'border-emerald-200', light: 'bg-emerald-50' },
  sky: { bg: 'bg-sky-600', text: 'text-sky-600', border: 'border-sky-200', light: 'bg-sky-50' },
  amber: { bg: 'bg-amber-600', text: 'text-amber-600', border: 'border-amber-200', light: 'bg-amber-50' },
  teal: { bg: 'bg-teal-600', text: 'text-teal-600', border: 'border-teal-200', light: 'bg-teal-50' },
  violet: { bg: 'bg-violet-600', text: 'text-violet-600', border: 'border-violet-200', light: 'bg-violet-50' },
};

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Claves culturales para trabajar en Suiza',
  description: 'Precisión, neutralidad, multilingüismo y protocolos de trabajo en Suiza. Guía para integrarse en el mercado laboral suizo con éxito.',
  url: `${siteUrl}/guia-claves-suiza`,
  author: { '@type': 'Organization', name: 'BN ĀGENZIA' },
  publisher: { '@type': 'Organization', name: 'BN ĀGENZIA', url: siteUrl },
  inLanguage: 'es',
  about: { '@type': 'Thing', name: 'Mercado laboral Suiza' },
  dateModified: '2025-07-14',
};

export default function GuiaClaveSuizaPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 print:bg-white">
      <SeoHead
        title="Claves culturales para trabajar en Suiza – Guía | BN ĀGENZIA"
        description="Precisión, neutralidad, multilingüismo y protocolos de trabajo en Suiza. Todo lo que necesitas para integrarte con éxito en el mercado laboral suizo."
        keywords="trabajar en Suiza, cultura laboral suiza, empleo Suiza, mercado laboral Suiza, integración profesional Suiza"
        canonical="/guia-claves-suiza"
        schema={schema}
      />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/perfil?tab=recursos')}
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#1A2B4A] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              Volver a recursos
            </button>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-rose-50 text-rose-700 border border-rose-200 rounded-full text-xs font-semibold">
                🇨🇭 Suiza
              </span>
              <button
                onClick={() => window.print()}
                className="flex items-center gap-2 px-4 py-2 bg-[#1A2B4A] text-white text-sm font-medium rounded-lg hover:bg-[#2A3B5A] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-printer-line"></i>
                Imprimir / Guardar PDF
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10 print:py-4 print:px-8">
        <div className="bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] rounded-2xl p-10 mb-10">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0 text-4xl">
              🇨🇭
            </div>
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1 bg-rose-400/20 text-rose-300 border border-rose-400/30 rounded-full text-xs font-semibold uppercase tracking-wide">
                  Guía cultural
                </span>
                <span className="text-white/50 text-xs">6 secciones · Lectura: ~12 min</span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
                Claves culturales para trabajar en Suiza
              </h1>
              <p className="text-white/80 text-base leading-relaxed max-w-2xl">
                Comprende la cultura corporativa suiza: precisión, neutralidad, multilingüismo y protocolos de trabajo en un entorno internacional de primer nivel.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-10">
          <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-list-check-2 text-[#1A2B4A]"></i>
            Contenido de la guía
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sections.map((section) => {
              const c = colorMap[section.color];
              return (
                <a key={section.id} href={`#seccion-${section.id}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <span className={`w-7 h-7 ${c.bg} text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0`}>
                    {section.id}
                  </span>
                  <span className="text-sm text-gray-700 group-hover:text-[#1A2B4A] font-medium transition-colors">
                    {section.title}
                  </span>
                </a>
              );
            })}
          </div>
        </div>

        <div className="space-y-8">
          {sections.map((section) => {
            const c = colorMap[section.color];
            return (
              <div key={section.id} id={`seccion-${section.id}`}
                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print:break-inside-avoid"
              >
                <div className={`${c.light} ${c.border} border-b px-6 py-5 flex items-center gap-4`}>
                  <div className={`w-11 h-11 ${c.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <i className={`${section.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <span className={`text-xs font-semibold ${c.text} uppercase tracking-wide`}>Sección {section.id}</span>
                    <h2 className="text-lg font-bold text-gray-900 leading-tight">{section.title}</h2>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  {section.content.map((item, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className={`w-1 rounded-full ${c.bg} flex-shrink-0 mt-1`} style={{ minHeight: '100%' }}></div>
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-1.5">{item.subtitle}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 bg-white rounded-xl border border-gray-200 shadow-sm p-8 print:break-inside-avoid">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 bg-[#1A2B4A] rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-multiple-line text-white text-xl"></i>
            </div>
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Antes de llegar</span>
              <h2 className="text-lg font-bold text-gray-900">Checklist esencial</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tips.map((tip, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                <i className="ri-check-double-line text-emerald-600 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-sm text-gray-700 leading-relaxed">{tip.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] rounded-xl p-8 text-center print:hidden">
          <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-customer-service-2-line text-white text-2xl"></i>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">¿Tienes dudas sobre el mercado suizo?</h3>
          <p className="text-white/80 text-sm mb-6 max-w-lg mx-auto leading-relaxed">
            Nuestro equipo tiene experiencia directa en el mercado laboral suizo y puede orientarte de forma personalizada.
          </p>
          <button
            onClick={() => navigate('/#contacto')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#1A2B4A] text-sm font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line"></i>
            Contactar con el equipo
          </button>
        </div>

        <div className="mt-8 text-center text-xs text-gray-400 pb-8 print:mt-4">
          <p>Guía elaborada por el equipo de BN Agenzia · Uso exclusivo para candidatos registrados</p>
        </div>
      </div>

      <style>{`
        @media print {
          @page { margin: 1.5cm; }
          body { font-size: 11pt; }
        }
      `}</style>
    </div>
  );
}
